/* eslint-disable @nx/enforce-module-boundaries */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { BaseCrudService } from '@t-llevo/common-ui';
import { Environment, ResponseData } from '@t-llevo/util';
import { apiDriverRoutes as apiRoutes } from '../routes/api-driver-routes';
import { Driver } from '../../common/models/Driver';
import { forkJoin, map, mergeMap, of, switchMap } from 'rxjs';
import { DriverRequestDTO } from '../../driver/dto/DriverRequestDTO';
import { AddDriverForm } from '../../driver/models/AddDriverForm';
import { EditDriverRequestDTO } from '../../driver/dto/EditDriverRequestDTO';
import { EditForm } from '../../driver/models/EditForm';
import { UploadImageService } from 'admin-drivers/src/app/api/services/upload-image.service';
import { UserService } from 'admin-drivers/src/app/api/services/user.service';
import { DriverRequest } from '../models/requests/DriverRequest';
import { UserRequest } from '../models/requests/UserRequest';
import { DriverResponse } from '../models/responses/DriverResponse';
import { DriverMapper } from '../mappers/DriverMapper';
import { VehicleService } from './vehicle.service';
import { UserResponse } from '../models/responses/UserResponse';

@Injectable()
export class DriverService extends BaseCrudService<
  Driver,
  ResponseData<DriverResponse>
> {
  private DriverReqDTO = inject(DriverRequestDTO);
  private DriverEditReqDTO = inject(EditDriverRequestDTO);

  private uploadImgSrv = inject(UploadImageService);
  private userSrv = inject(UserService);
  private vehicleSrv = inject(VehicleService);

  constructor(@Inject(Environment) env: Environment, http: HttpClient) {
    super(env, http);
  }

  override transform(data: ResponseData<DriverResponse>) {
    return {
      ...data,
      results: data.results.map((driver) => {
        return DriverMapper(driver);
      }),
    };
  }

  createDriver(data: AddDriverForm) {
 

    const licensesImages = data.licenses.map((license) => license.image);

    return forkJoin({
      driverPhoto: this.uploadImgSrv.uploadDriverImage(data.personal.photo),
      licensesPhotos: this.uploadImgSrv.uploadLicensesImages(licensesImages),
      vehicle: this.vehicleSrv.createVehicle(data.vehicle),
    }).pipe<DriverRequest, DriverResponse>(
      map((obsData) => {
        return this.DriverReqDTO.mapTo({
          driver: data,
          ...obsData,
        });
      }),
      mergeMap((data) => {
        return this.http.post<DriverResponse>(
          this.env.apiUrl + apiRoutes.routeDrivers,
          data,
        );
      }),
    );
  }

  updateDriver(
    data: EditForm,
    id: { driverId: string; vehicleId: string; userId: string },
  ) {
    const licensesImages = data.licenses.map((lic) => lic.image);

    return forkJoin({
      licenseImages: this.uploadImgSrv.uploadLicensesImages(licensesImages),
      driverPhoto: this.uploadImgSrv.uploadDriverImage(data.personal.photo),
    }).pipe(
      map((obsData) =>
        this.DriverEditReqDTO.mapTo({
          data,
          licensesPhoto: obsData.licenseImages,
          driverPhoto: obsData.driverPhoto,
        }),
      ),
      switchMap((formatedData) =>
        forkJoin({
          vehicle: this.vehicleSrv.updateVehicle(
            formatedData.vehicle,
            id.vehicleId,
          ),
          driver: this.http.patch(
            this.env.apiUrl + apiRoutes.routeDrivers + `/${id.driverId}`,
            {
              licences: formatedData['licences'],
              photo: formatedData['photo'],
              birthday: formatedData['birthday'],
              ci: formatedData['ci'],
            },
          ),
          user: this.userSrv.updateUserById(formatedData.user, id.userId),
        }),
      ),
    );
  }

  deleteDriver(driver: Driver) {
    return forkJoin({
      deleteVehicle: this.vehicleSrv.deleteVehicle(driver.vehicle.id),
      deleteDriver: this.http.delete(
        this.env.apiUrl + apiRoutes.routeDrivers + `/${driver.id}`,
      ),
      deleteUser: this.userSrv.deleteUserById(driver.user.id),
    });
  }

  private getUsersData() {
    return this.userSrv
      .getUsers()
      .pipe(
        map((users) => users.results.filter((user) => user.roles.includes('driver'))),
      );
  }
}
